/**
 * Agrupa en la LISTA de una seccion los registros que Atenea marco como iguales (texto, unidad y precio)
 * (MADERINCO-VARIANTES-PANTALLA-T1). Solo presentacion:
 *   - UN item por grupo (Ares #30338, Josse: sin selector de fuentes); las fuentes quedan en el Detalle de la fila;
 *     al agregar entra el id EXACTO de la primera fuente en el orden de la lista; nada se borra en la base;
 *   - se agrupa solo lo que esta en la misma lista (nunca entre negocios ni entre secciones);
 *   - solo con el MISMO precio por unidad; un precio desconocido nunca se iguala a otro (tambien en el grupo por area);
 *   - en el arbol de Cubierta (medidas: true) tambien la misma solucion que solo cambia los numeros de la descripcion,
 *     con la misma unidad y el mismo precio.
 *
 * @module application/presupuestos/variantesDeLaLista
 */
import { GRUPOS } from './gruposDeVariantes.js';

const grupoDe = new Map();
for (const g of GRUPOS) for (const f of g.fuenteIds) grupoDe.set(f, g.id);

/**
 * @param {Array<{id:string, fuenteId?:string, precioRef?:number|null}>} items la lista visible, en su orden
 * @returns {Array<{item:object, fuentes:object[]}>} una entrada por fila; fuentes.length > 1 = grupo
 */
export function agruparVariantes(items, { medidas = false } = {}) {
  const filas = [];
  const porClave = new Map();
  for (const it of items || []) {
    const g = it.fuenteId ? grupoDe.get(it.fuenteId) : null;
    const precio = it.precioRef == null ? 'sin-precio:' + it.id : String(it.precioRef);
    // Ares #30338 (Josse): UN item solo si el precio por unidad es el MISMO -- tambien en el grupo por area.
    // medidas (arbol de Cubierta): la misma solucion que solo cambia los numeros de la descripcion (259 vs 240 m2),
    // con la misma unidad y el mismo precio, es UN item aunque Atenea no la haya agrupado.
    const clave = g ? g + '|' + precio
      : (medidas ? 'medidas|' + textoSinMedidas(it.descripcion) + '|' + String(it.unidad || '').toLowerCase() + '|' + precio : null);
    if (clave && porClave.has(clave)) { porClave.get(clave).fuentes.push(it); continue; }
    const fila = { item: it, fuentes: [it] };
    if (clave) porClave.set(clave, fila);
    filas.push(fila);
  }
  return filas;
}

/** La descripcion sin numeros ni medidas: "Cubierta ... 259 m2" y "Cubierta ... 240 m2" quedan iguales. */
export function textoSinMedidas(s) {
  // Obs de Q en 470 (Ares #30353, plan 20-oct): quitar SOLO la medida del proyecto -- un numero seguido de m2, m²,
  // mts2, metros cuadrados, ml o metros lineales --. Los numeros de especificacion (calibre 22, 17 mm, 2 1/2",
  // seccion 50 x 30, "3 m de largo" de una pieza) se conservan: partidas que difieren en eso NO son la misma solucion.
  return String(s ?? '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/\d+(?:[.,]\d+)?\s*(?:m2|m²|mts2|mt2|metros? cuadrados?|ml|metros? lineales?)(?![a-z0-9])/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ').trim();
}

/** true si las fuentes de una fila no tienen todas el mismo precio (el selector entonces muestra el de cada una). */
export function preciosDistintos(fuentes) {
  return new Set((fuentes || []).map((f) => String(f.precioRef ?? ''))).size > 1;
}

/** El nombre corto de la fuente para el selector: el libro y la celda del id del catalogo ("1346 1C:B12"). */
export function nombreDeFuente(it) {
  const f = String(it.fuenteId || it.id || '');
  const [libro, hoja, celda] = f.split(':');
  if (!celda) return f;
  const h = String(hoja).replace(/^PRESUPUESTO\s*-?\s*/i, '').trim();
  return `${libro} ${h ? h + ':' : ''}${celda}`;
}
